import React from 'react';
import { useParams, Link } from 'react-router-dom';

const CollectionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  // Collection data - in a real app, this would come from an API
  const collections = {
    'museo': {
      id: 'museo',
      name: 'Museo Textiles',
      category: 'heritage',
      description: 'Timeless patterns inspired by classical art and architecture',
      longDescription: 'The Museo collection represents the pinnacle of classical textile design, drawing inspiration from the world\'s most prestigious museums and architectural masterpieces. Each pattern tells a story of human creativity across centuries, from the geometric precision of Islamic art to the flowing elegance of Renaissance motifs.',
      image: '/images/Buy Clothing Fabrics by Category, Online Shop - SK Textiles/imgi_20_CHIFFON-WOVEN_WHITE_YS135Z150-03000-450x450.jpg',
      gradient: 'from-royal-purple to-amethyst',
      images: [
        '/images/Buy Clothing Fabrics by Category, Online Shop - SK Textiles/imgi_20_CHIFFON-WOVEN_WHITE_YS135Z150-03000-450x450.jpg',
        '/images/Buy Clothing Fabrics by Category, Online Shop - SK Textiles/imgi_7_CHIFFON-WOVEN_WHITE_YS135Z150-03000-300x300.jpg',
        '/images/Buy Clothing Fabrics by Category, Online Shop - SK Textiles/imgi_18_CHIFFON-WOVEN_LILLY-PRINT_WPPR1506-17101-450x450.jpg',
        '/images/Buy Clothing Fabrics by Category, Online Shop - SK Textiles/imgi_16_CHIFFON-WOVEN_BLACK_SG-JQ1967-01000-450x450.jpg',
        '/images/Buy Clothing Fabrics by Category, Online Shop - SK Textiles/imgi_27_CHIFFON-WOVEN_SWEET-MELON_YS135Z150-09010-450x450.jpg'
      ],
      specifications: {
        'Material': '100% Organic Cotton',
        'Weight': '280 GSM',
        'Width': '150cm',
        'Pattern Repeat': '64cm',
        'Care Instructions': 'Dry clean only',
        'Sustainability': 'GOTS Certified'
      },
      features: [
        'Classical geometric patterns',
        'Timeless color palette',
        'Premium organic materials',
        'Heritage-inspired designs',
        'Versatile applications',
        'Long-lasting durability'
      ],
      applications: ['Upholstery', 'Drapery', 'Wall Coverings', 'Accent Pillows', 'Table Linens']
    },
    'chintz': {
      id: 'chintz',
      name: 'Chintz Collection',
      category: 'luxury',
      description: 'Exquisite floral patterns with rich, vibrant colors',
      longDescription: 'The Chintz collection celebrates the art of floral design with patterns that capture the beauty of nature in its most vibrant form. Inspired by traditional Indian chintz techniques, these fabrics feature hand-drawn botanical motifs that bring life and color to any interior space.',
      image: '/images/Print Fabrics – Lively Fabrics/imgi_28_9168bb4e-1d43-4a2c-8fb8-cc7c516f0caa.jpg',
      gradient: 'from-warm-gold to-sunset-orange',
      images: [
        '/images/Print Fabrics – Lively Fabrics/imgi_28_9168bb4e-1d43-4a2c-8fb8-cc7c516f0caa.jpg',
        '/images/Print Fabrics – Lively Fabrics/imgi_15_6e953e97-6090-48d7-a413-57325e969c2d.jpg',
        '/images/Print Fabrics – Lively Fabrics/imgi_23_b1dd0899-f81b-4ea5-a83c-4017543271b5.jpg',
        '/images/Print Fabrics – Lively Fabrics/imgi_20_286a5407-307d-43d3-bcec-07217c2367e5.jpg',
        '/images/Print Fabrics – Lively Fabrics/imgi_17_ea887dd6-8120-497d-b092-99b60036df43.jpg'
      ],
      specifications: {
        'Material': 'Silk-Cotton Blend',
        'Weight': '320 GSM',
        'Width': '140cm',
        'Pattern Repeat': '72cm',
        'Care Instructions': 'Professional cleaning recommended',
        'Sustainability': 'OEKO-TEX Certified'
      },
      features: [
        'Hand-drawn floral patterns',
        'Rich, vibrant color palette',
        'Luxury silk-cotton blend',
        'Traditional chintz techniques',
        'Statement-making designs',
        'Premium finishing'
      ],
      applications: ['Upholstery', 'Drapery', 'Bedding', 'Table Cloths', 'Decorative Pillows']
    },
    'damask': {
      id: 'damask',
      name: 'Damask Patterns',
      category: 'luxury',
      description: 'Sophisticated geometric designs for premium applications',
      longDescription: 'The Damask collection features sophisticated geometric patterns that have graced the homes of nobility for centuries. These timeless designs combine mathematical precision with artistic elegance, creating fabrics that serve as the foundation for luxurious interior spaces.',
      image: '/images/Caversham Textiles and Designs – Modern South African Designs/imgi_136_HC-Whole-Fig-Tea-Towel-02-SQ.jpg',
      gradient: 'from-coral to-rose-500',
      images: [
        '/images/Caversham Textiles and Designs – Modern South African Designs/imgi_136_HC-Whole-Fig-Tea-Towel-02-SQ.jpg',
        '/images/Caversham Textiles and Designs – Modern South African Designs/imgi_152_HC-Pomegranate-Tea-Towel-01-SQ.jpg',
        '/images/Caversham Textiles and Designs – Modern South African Designs/imgi_144_HC-Cut-Fig-Tea-Towel-01-SQ.jpg',
        '/images/Caversham Textiles and Designs – Modern South African Designs/imgi_112_PF-Wallpaper-1.jpg',
        '/images/Caversham Textiles and Designs – Modern South African Designs/imgi_102_Lounge-Paradise-Floral-a.jpg'
      ],
      specifications: {
        'Material': '100% Linen',
        'Weight': '350 GSM',
        'Width': '160cm',
        'Pattern Repeat': '80cm',
        'Care Instructions': 'Dry clean only',
        'Sustainability': 'GOTS Certified Linen'
      },
      features: [
        'Classical geometric patterns',
        'Premium linen material',
        'Sophisticated color schemes',
        'Timeless elegance',
        'Versatile applications',
        'Luxury finishing'
      ],
      applications: ['Upholstery', 'Drapery', 'Table Linens', 'Bedding', 'Wall Coverings']
    },
    'chenille': {
      id: 'chenille',
      name: 'Chenille Fabric',
      category: 'upholstery',
      description: 'Luxuriously soft and durable for furniture and decor',
      longDescription: 'The Chenille collection offers the perfect balance of luxury and practicality. These velvety-soft fabrics are designed for everyday comfort while maintaining the sophisticated aesthetic that defines premium upholstery. Perfect for both residential and commercial applications.',
      image: '/images/Plain-Mini-Matt-Fabrics/imgi_221_MiniMatt_Colour07_Twist-Edit.jpg',
      gradient: 'from-ocean-blue to-teal-wave',
      images: [
        '/images/Plain-Mini-Matt-Fabrics/imgi_221_MiniMatt_Colour07_Twist-Edit.jpg',
        '/images/Plain-Mini-Matt-Fabrics/imgi_251_MiniMattPlain_ColName-Orange_ColNo-15_Twist.jpg',
        '/images/Plain-Mini-Matt-Fabrics/imgi_322_MiniMatt_Colour17_Twist.jpg',
        '/images/Plain-Mini-Matt-Fabrics/imgi_241_plainminiMattsoftberry_Twist.jpg',
        '/images/Plain-Mini-Matt-Fabrics/imgi_231_plainminiMattbeige_Twist.jpg'
      ],
      specifications: {
        'Material': 'Cotton-Polyester Blend',
        'Weight': '400 GSM',
        'Width': '150cm',
        'Pattern Repeat': 'N/A',
        'Care Instructions': 'Spot clean, professional cleaning for stains',
        'Sustainability': 'Recycled Polyester Content'
      },
      features: [
        'Luxuriously soft texture',
        'High durability rating',
        'Stain-resistant finish',
        'Versatile color options',
        'Easy maintenance',
        'Commercial grade'
      ],
      applications: ['Furniture Upholstery', 'Decorative Pillows', 'Ottomans', 'Headboards', 'Accent Chairs']
    },
    'fire-retardant': {
      id: 'fire-retardant',
      name: 'Fire-retardant',
      category: 'technical',
      description: 'Safety-focused fabrics for commercial and public spaces',
      longDescription: 'The Fire-retardant collection prioritizes safety without compromising on style. These technically advanced fabrics meet the highest fire safety standards while maintaining the aesthetic appeal required for modern commercial and public spaces.',
      image: '/images/The Stain Guard Range™️_ Protection🍷 – Ragland Fabrics/imgi_259_MarsalaTS.png',
      gradient: 'from-deep-purple to-royal-purple',
      images: [
        '/images/The Stain Guard Range™️_ Protection🍷 – Ragland Fabrics/imgi_259_MarsalaTS.png',
        '/images/The Stain Guard Range™️_ Protection🍷 – Ragland Fabrics/imgi_239_GreyscaleTS.png',
        '/images/The Stain Guard Range™️_ Protection🍷 – Ragland Fabrics/imgi_249_JasperTS.png',
        '/images/The Stain Guard Range™️_ Protection🍷 – Ragland Fabrics/imgi_289_Timber_WolfTS.png',
        '/images/The Stain Guard Range™️_ Protection🍷 – Ragland Fabrics/imgi_309_GrassTS_083dcb1a-36b3-4452-95e5-c05fc70866a9.png'
      ],
      specifications: {
        'Material': 'Aramid Fiber Blend',
        'Weight': '450 GSM',
        'Width': '150cm',
        'Pattern Repeat': 'N/A',
        'Care Instructions': 'Professional cleaning only',
        'Sustainability': 'Recycled Content Available'
      },
      features: [
        'Class A fire rating',
        'High durability',
        'Commercial grade',
        'Multiple color options',
        'Easy maintenance',
        'Safety certified'
      ],
      applications: ['Hotel Upholstery', 'Office Furniture', 'Public Transportation', 'Healthcare Facilities', 'Educational Institutions']
    },
    'polo': {
      id: 'polo',
      name: 'Polo T-shirt',
      category: 'apparel',
      description: 'Premium cotton blends for comfortable, stylish clothing',
      longDescription: 'The Polo T-shirt collection represents the perfect marriage of comfort and style. These premium fabrics are designed for everyday wear, offering exceptional breathability, durability, and a luxurious feel that elevates casual clothing to new heights.',
      image: '/images/Online Clothing Fabrics Shop - Vega Textiles/imgi_149_IMG_1320.jpg',
      gradient: 'from-sky-blue to-ocean-blue',
      images: [
        '/images/Online Clothing Fabrics Shop - Vega Textiles/imgi_149_IMG_1320.jpg',
        '/images/Online Clothing Fabrics Shop - Vega Textiles/imgi_108_IMG_2366.jpg',
        '/images/Online Clothing Fabrics Shop - Vega Textiles/imgi_131_IMG_1307.jpg',
        '/images/Online Clothing Fabrics Shop - Vega Textiles/imgi_164_IMG_2297.jpg',
        '/images/Online Clothing Fabrics Shop - Vega Textiles/imgi_154_IMG_1303.jpg'
      ],
      specifications: {
        'Material': 'Organic Cotton-Pima Blend',
        'Weight': '180 GSM',
        'Width': '180cm',
        'Pattern Repeat': 'N/A',
        'Care Instructions': 'Machine wash cold, tumble dry low',
        'Sustainability': 'GOTS Certified Organic'
      },
      features: [
        'Premium cotton blend',
        'Exceptional breathability',
        'Soft, luxurious feel',
        'Easy care maintenance',
        'Versatile styling',
        'Sustainable materials'
      ],
      applications: ['Polo Shirts', 'T-shirts', 'Casual Shirts', 'Loungewear', 'Athletic Wear']
    }
  };

  const collection = collections[id as keyof typeof collections];

  if (!collection) {
    return (
      <div className="min-h-screen pt-32 pb-20 bg-gradient-primary">
        <div className="container-custom text-center">
          <h1 className="text-4xl font-serif font-bold text-gradient mb-4">Collection Not Found</h1>
          <p className="text-sage mb-8">The collection you're looking for doesn't exist.</p>
          <Link to="/collections" className="btn-primary">Back to Collections</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-midnight via-deep-purple to-royal-purple">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1582735689369-4fe89db7114c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center bg-no-repeat opacity-20"></div>
        </div>

        <div className="container-custom relative z-10">
          <div className="max-w-6xl mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center justify-center space-x-2 mb-8">
              <Link to="/" className="text-lavender/60 hover:text-lavender">Home</Link>
              <span className="text-lavender/40">/</span>
              <Link to="/collections" className="text-lavender/60 hover:text-lavender">Collections</Link>
              <span className="text-lavender/40">/</span>
              <span className="text-lavender">{collection.name}</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* Left Column - Main Image */}
              <div className="relative">
                <div className="relative overflow-hidden rounded-3xl shadow-2xl">
                  <img
                    src={collection.image}
                    alt={collection.name}
                    className="w-full h-96 object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className={`bg-gradient-to-r ${collection.gradient} text-white px-4 py-2 text-sm font-medium rounded-full shadow-lg`}>
                      {collection.category}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right Column - Collection Info */}
              <div className="text-white">
                <h1 className="text-4xl md:text-6xl font-serif font-bold mb-6 animate-fade-in drop-shadow-2xl">
                  {collection.name}
                </h1>
                <p className="text-xl text-lavender mb-6 font-medium animate-slide-up drop-shadow-xl">
                  {collection.description}
                </p>
                <p className="text-lg text-white/90 mb-8 leading-relaxed animate-slide-up drop-shadow-lg">
                  {collection.longDescription}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 animate-slide-up">
                  <button className="px-8 py-4 bg-gradient-to-r from-royal-purple to-amethyst text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 border border-royal-purple/30">
                    Request Sample
                  </button>
                  <button className="px-8 py-4 bg-gradient-to-r from-white/20 to-white/10 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 border border-white/30 backdrop-blur-sm">
                    Download Specs
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-gradient-primary">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-gradient mb-4">
              Collection Gallery
            </h2>
            <p className="text-lg text-sage max-w-2xl mx-auto">
              Explore the beauty and versatility of {collection.name}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {collection.images.map((image, index) => (
              <div key={index} className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500">
                <img
                  src={image}
                  alt={`${collection.name} - View ${index + 1}`}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-midnight/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specifications & Features */}
      <section className="py-20 bg-gradient-secondary">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Specifications */}
            <div>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-gradient mb-8">
                Technical Specifications
              </h2>
              <div className="space-y-4">
                {Object.entries(collection.specifications).map(([key, value]) => (
                  <div key={key} className="flex justify-between items-center py-3 border-b border-lavender/20">
                    <span className="font-semibold text-charcoal">{key}</span>
                    <span className="text-sage">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Features */}
            <div>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-gradient mb-8">
                Key Features
              </h2>
              <div className="grid grid-cols-1 gap-4">
                {collection.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-gradient-to-r from-royal-purple to-amethyst rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    <span className="text-sage">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="py-20 bg-gradient-accent">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-gradient mb-4">
              Perfect For
            </h2>
            <p className="text-lg text-sage max-w-2xl mx-auto">
              Discover the versatile applications of {collection.name}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {collection.applications.map((application, index) => (
              <div key={index} className="glass-card p-6 text-center group hover:scale-105 transition-transform duration-300">
                <div className="w-16 h-16 bg-gradient-to-br from-royal-purple/20 to-amethyst/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">✨</span>
                </div>
                <h3 className="text-lg font-serif font-semibold text-charcoal">{application}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-primary">
        <div className="container-custom text-center">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-gradient mb-6">
            Ready to Experience {collection.name}?
          </h2>
          <p className="text-xl text-sage mb-8 max-w-2xl mx-auto">
            Contact our team to discuss your specific needs and discover how {collection.name} can bring your vision to life.
          </p>
          <div className="flex justify-center">
            <Link 
              to="/contact" 
              className="btn-primary"
            >
              Request Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CollectionDetailPage;
